import type { TransferBadgeVariant, TransferDetailState } from "@/types";

/**
 * The gateway emits `status` as a composite-status wire value (see
 * TransferCompositeStatus). Map that wire value to the badge variant, the
 * human label, and the detail-page state. The gateway only ever projects a
 * handful of these today (`pending-policy`, `rejected`, `failed`,
 * `outcome-unknown`), but the map covers the full status enum so the list
 * degrades gracefully as more states land.
 */
export function transferStatusVariant(status: string): TransferBadgeVariant {
  const s = status.toLowerCase();
  if (s.includes("reconcil") || s.includes("confirm")) return "success";
  if (s.includes("fail") || s.includes("reject")) return "danger";
  if (s.includes("signing") || s.includes("broadcast") || s.includes("approved")) return "info";
  if (s.includes("pending") || s.includes("outcome")) return "warning";
  return "neutral";
}

const TRANSFER_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  "pending-policy": "Evaluating Policy",
  "pending-approval": "Awaiting Approval",
  approved: "Approved",
  // ALL-4884: Lane B's real wire value may use either separator; both keys
  // are listed so a literal-key lookup hits regardless.
  "pending-device-signature": "Waiting for the signer's phone",
  "pending_device_signature": "Waiting for the signer's phone",
  rejected: "Rejected",
  signing: "Signing",
  broadcasting: "Broadcasting",
  broadcast: "Broadcast",
  confirmed: "Confirmed",
  reconciled: "Reconciled",
  failed: "Failed",
  "outcome-unknown": "Outcome Unknown",
};

export function transferStatusLabel(status: string): string {
  const s = status.toLowerCase();
  return TRANSFER_STATUS_LABELS[s] ?? s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function transferStatusState(status: string): TransferDetailState {
  const s = status.toLowerCase();
  if (s.includes("reconcil")) return "reconciled";
  if (s.includes("reject")) return "rejected";
  if (s.includes("fail")) return "failed";
  // ALL-4884: approvals cleared, waiting on the paired phone's co-signature
  // (pending_device_signature / pending-device-signature). Checked before the
  // generic "approval" match below, which this string would also satisfy.
  if (s.includes("device")) return "pending-device-signature";
  if (s.includes("approval")) return "pending-approval";
  if (s.includes("screening") || s.includes("policy")) return "pending-policy";
  if (s.includes("signing") || s.includes("approved")) return "approved";
  // Split broadcast vs. confirmed into distinct states (ALL-4884) so each
  // gets its own label/timeline entry; both still show the finality panel
  // (tx hash + Etherscan link) once Mesh returns one.
  if (s.includes("confirm")) return "confirmed";
  if (s.includes("broadcast")) return "broadcast";
  if (s.includes("outcome")) return "outcome-unknown";
  if (s.includes("draft")) return "draft";
  return "pending-policy";
}

export const TRANSFER_DETAIL_STATES: TransferDetailState[] = [
  "draft",
  "pending-policy",
  "pending-approval",
  "approved",
  "pending-device-signature",
  "broadcast",
  "confirmed",
  "broadcast-confirmed",
  "reconciled",
  "rejected",
  "failed",
  "outcome-unknown",
];

export function isTransferDetailState(value: string | undefined): value is TransferDetailState {
  return !!value && (TRANSFER_DETAIL_STATES as string[]).includes(value);
}

export function resolveTransferDetailState(
  raw: string | undefined,
  fallback: TransferDetailState,
): TransferDetailState {
  return isTransferDetailState(raw) ? raw : fallback;
}

export const TRANSFER_STATE_LABEL: Record<TransferDetailState, string> = {
  draft: "Draft",
  "pending-policy": "Pending Policy",
  "pending-approval": "Pending Approval",
  approved: "Approved",
  "pending-device-signature": "Waiting for the signer's phone",
  // ALL-4884: the same transfer reads "Broadcasting" in the list and in the
  // status filter (transferStatusLabel's "broadcasting" entry, and
  // pages/enterprise/transfers.tsx's STATUSES), so the detail page says the
  // same word for the same transfer rather than a second name for it.
  broadcast: "Broadcasting",
  confirmed: "Confirmed",
  "broadcast-confirmed": "Broadcast Confirmed",
  reconciled: "Reconciled",
  rejected: "Rejected",
  failed: "Failed",
  "outcome-unknown": "Outcome Unknown",
};

/** Wizard step resolver — kept out of the client module so the server page can call it. */
export const TRANSFER_WIZARD_STEPS = [1, 2, 3, 4, 5] as const;
export type TransferWizardStep = (typeof TRANSFER_WIZARD_STEPS)[number];

export function resolveTransferWizardStep(raw: string | undefined): TransferWizardStep {
  const n = Number(raw);
  return (TRANSFER_WIZARD_STEPS as readonly number[]).includes(n) ? (n as TransferWizardStep) : 1;
}

/**
 * List groups for the transfers page (ALL-5793). Each public state belongs to exactly one group,
 * so the group counts add up to the number of transfers loaded.
 */
export type TransferGroup = "awaiting-approval" | "in-progress" | "completed" | "stopped";

export const TRANSFER_GROUP_LABEL: Record<TransferGroup, string> = {
  "awaiting-approval": "Awaiting approval",
  "in-progress": "In progress",
  completed: "Completed",
  stopped: "Failed or rejected",
};

export function transferGroupOf(state: TransferDetailState): TransferGroup {
  switch (state) {
    case "pending-approval":
      return "awaiting-approval";
    case "confirmed":
    case "broadcast-confirmed":
    case "reconciled":
      return "completed";
    case "failed":
    case "rejected":
    case "outcome-unknown":
      return "stopped";
    default:
      // draft, pending-policy, approved/signing, pending-device-signature, broadcast
      return "in-progress";
  }
}
